import React from 'react';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useRecoilState } from 'recoil';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Form from 'pages/myidm/components/Form';
import { OrderPayload } from 'pages/myidm/store';
import { AD_GROUP, REQUEST_TYPE } from 'pages/myidm/utils/constant';
import SelectMemberBtn from './SelectMemberBtn';
import MembersListModal from './MembersListModal';

const ResourceSection = () => {
    const [order, setOrder] = useRecoilState(OrderPayload);

    const handleAddResourceGroup = () => {
        const updatedGroups = [...(order?.multipleResourceGroups || [])];
        updatedGroups.push({
            groupCategory: null,
            groupSubCategory: null,
            activeDirectoryGroupType: null,
            domainType: null,
            groupOwner: null,
            groupName: null,
            members: [],
        });
        setOrder({
            ...order,
            multipleResourceGroups: updatedGroups,
        });
    };

    const handleDeleteResourceGroup = (index) => {
        const updatedGroups = [...(order?.multipleResourceGroups || [])];
        updatedGroups.splice(index, 1);
        setOrder({
            ...order,
            multipleResourceGroups: updatedGroups,
        });
    };

    // Reset resource groups array when conditions change
    React.useEffect(() => {
        if (order?.typeOfRequest === REQUEST_TYPE.NEW && order?.groupType === AD_GROUP) {
            if (!order?.multipleResourceGroups || order?.multipleResourceGroups?.length === 0) {
                setOrder({
                    ...order,
                    multipleResourceGroups: [
                        {
                            groupCategory: null,
                            groupSubCategory: null,
                            activeDirectoryGroupType: null,
                            domainType: null,
                            groupOwner: null,
                            groupName: null,
                            members: [],
                        },
                    ],
                });
            }
        }
    }, [order?.typeOfRequest, order?.groupType]);

    if (order?.typeOfRequest !== REQUEST_TYPE.NEW || order?.groupType !== AD_GROUP) {
        return null;
    }

    return (
        <>
            {/* Max limit message */}
            <Grid item xs={12}>
                {(order?.multipleResourceGroups?.length || 0) < 10 && (
                    <Typography variant="subtitle2" sx={{ fontSize: '12px' }}>
                        You can add up to 10 resource groups.
                    </Typography>
                )}
                {(order?.multipleResourceGroups?.length || 0) >= 10 && (
                    <Typography variant="subtitle2" sx={{ fontSize: '12px', color: '#d40020' }} data-testid="resource-groups-max-limit-reached-error-txt">
                        You have reached max limit, you can add up to 10 resource groups.
                    </Typography>
                )}
            </Grid>

            {/* Resource Groups Added Header */}
            <Grid item xs={12}>
                <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#000' }}>
                    Resource Groups Added ({order?.multipleResourceGroups?.length || 0})
                </Typography>
                <Divider variant="middle" />
            </Grid>

            {/* Render resource groups based on multipleResourceGroups array */}
            {(order?.multipleResourceGroups || []).map((_, index) => (
                <React.Fragment key={`resource-group-fragment-${index}`}>
                    <Grid item xs={6} key={`group-category-${index}`}>
                        <Form.Resource.GroupCategory index={index} />
                    </Grid>
                    <Grid item xs={6} key={`group-subcategory-${index}`}>
                        <Form.Resource.GroupSubCategory index={index} />
                    </Grid>
                    <Grid item xs={6} key={`cloud-type-${index}`}>
                        <Form.Resource.CloudType index={index} />
                    </Grid>
                    <Grid item xs={6} key={`ad-group-type-${index}`}>
                        <Form.Resource.ActiveDirectoryGroupType index={index} />
                    </Grid>
                    <Grid item xs={6} key={`domain-type-${index}`}>
                        <Form.Resource.DomainType index={index} />
                    </Grid>
                    <Grid item xs={6} key={`group-owner-${index}`}>
                        <Form.Resource.GroupOwner label="Group Owner *" index={index} />
                    </Grid>
                    <Grid item xs={6} key={`group-name-${index}`}>
                        <Form.Resource.GroupName index={index} />
                    </Grid>

                    {/* Select Member button */}
                    <Grid item xs={12} key={`select-member-container-${index}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SelectMemberBtn index={index} />
                        </Box>
                    </Grid>

                    {/* Delete button */}
                    <Grid item xs={12} key={`delete-btn-container-${index}`} sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1, mb: 1 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteResourceGroup(index)}
                            sx={{ minWidth: '42px', width: '42px', height: '32px' }}
                            disabled={(order?.multipleResourceGroups?.length || 0) === 1}
                            data-testid={`delete-resource-group-btn-${index}`}
                        >
                            <DeleteIcon sx={{ fontSize: '25px' }} />
                        </Button>
                    </Grid>

                    {/* Divider between groups (except last one) */}
                    {index < (order?.multipleResourceGroups?.length || 0) - 1 && (
                        <Grid item xs={12} key={`divider-${index}`}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>
                    )}
                </React.Fragment>
            ))}

            {/* Add button */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="cmpPrimary"
                        onClick={handleAddResourceGroup}
                        sx={{ minWidth: '42px', width: '42px', height: '32px' }}
                        disableFocusRipple
                        disableRipple
                        data-testid="add-resource-group-btn"
                        disabled={(order?.multipleResourceGroups?.length || 0) >= 10}
                    >
                        <AddIcon sx={{ fontSize: '25px' }} />
                    </Button>
                </Box>
            </Grid>
            <MembersListModal />
        </>
    );
};

export default ResourceSection;
